import { type ReactNode, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { SetupProvider } from "@/core/providers";
import { isLoggedIn } from "@/features/authentication";
import { CollaborationProvider } from "@/features/collaboration";
import { SubscriptionProvider } from "./subscription.provider";
import { useSalesforceIframeGuard } from "@/core/hooks/use-salesforce-iframe-guard";
import { savePostLoginRedirect } from "@/core/utils/post-login-redirect.utils";
import {
  isSalesforceIframe,
  getSalesforceParams,
} from "@/core/utils/salesforce-iframe.utils";

interface ProtectedRouteProps {
  children?: ReactNode;
}

const AUTH_PAGES = ["/login", "/sign-up"];

const ProtectedRouteProvider = ({ children }: ProtectedRouteProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<{
    loading: boolean;
    authenticated: boolean;
  }>({
    loading: true,
    authenticated: false,
  });

  // Activate Salesforce iframe guard
  useSalesforceIframeGuard(authState.authenticated, !authState.loading);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authenticated = await isLoggedIn();
        setAuthState({ loading: false, authenticated });
      } catch (error) {
        console.error("Authentication check failed:", error);
        setAuthState({ loading: false, authenticated: false });
      }
    };

    checkAuth();
  }, []);

  // Redirect authenticated users to Salesforce integration page when in iframe
  useEffect(() => {
    if (authState.loading) return;
    if (!authState.authenticated) return;
    if (!isSalesforceIframe()) return;

    const currentPath = location.pathname;
    const salesforcePath = "/pages/integrations/salesforce";

    // If authenticated and in Salesforce iframe, redirect to integration page
    // unless already on auth pages or Salesforce page
    if (currentPath !== salesforcePath && !AUTH_PAGES.includes(currentPath)) {
      const salesforceParams = getSalesforceParams();
      navigate(`${salesforcePath}?${salesforceParams}`, { replace: true });
    }
  }, [authState.loading, authState.authenticated, location.pathname, navigate]);

  if (authState.loading) return null;

  if (!authState.authenticated) {
    if (!AUTH_PAGES.includes(location.pathname)) {
      // Save intended URL so we can redirect back after login
      savePostLoginRedirect(location.pathname + location.search);

      // Send flow: an invite to a guest or a new team member (?invite=1) is
      // someone with no login yet; Sign up returns them to the document.
      if (new URLSearchParams(location.search).has("invite")) {
        return <Navigate to="/sign-up" state={{ from: location }} replace />;
      }

      // Preserve integration params when redirecting to login
      const loginSearch = isSalesforceIframe()
        ? `?${getSalesforceParams()}`
        : location.search || "";

      return (
        <Navigate
          to={`/login${loginSearch}`}
          state={{ from: location }}
          replace
        />
      );
    }
    return <>{children}</>;
  }

  return (
    <SetupProvider>
      <SubscriptionProvider>
        <CollaborationProvider>
          {children ? children : <Outlet />}
        </CollaborationProvider>
      </SubscriptionProvider>
    </SetupProvider>
  );
};

export default ProtectedRouteProvider;
